
import { Component, Vue } from "vue-property-decorator";
import { UserInfo, UserTypeFilterEnum } from "@/store/types";
import { Getter, namespace } from "vuex-class";
import NavHeader from "@/components/NavHeader.vue";
import { Datetime } from "vue-datetime";
import { formatDateMixin, stringMixin } from "@/mixins";
import ActivityLogTable from "@/components/activity-log/ActivityLogTable.vue";
import vSelect3 from "vselect3";
import EventOutcomeMultiSelect from "@/components/form/EventOutcomeMultiSelect.vue";
import { AreaFilterItem } from "@/types/area-tree-select/AreaFilter";
import AdvancedAreaFilter from "@/components/AdvancedAreaFilter.vue";
import api from "@/services/api.service";
import { ActivityLogRequest, EventType } from "@/store/activity-log/types";
import { debounce } from "lodash";
import axios, { CancelTokenSource } from "axios";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import EventTypes from "@/types/sv-data/enums/EventTypes";
import { truncateString } from "@/filters";

const EventSearchStore = namespace("eventSearch");

Vue.mixin(formatDateMixin);
const { isNullOrWhitespace } = stringMixin.methods;

@Component({
	components: {
		"nav-header": NavHeader,
		Datetime,
		"v-select-3": vSelect3,
		ActivityLogTable,
		"advanced-area-selector": AdvancedAreaFilter,
		"event-outcome-multi-select": EventOutcomeMultiSelect,
	},
	filters: { truncateString },
})

export default class ActivityLogManagement extends Vue {
	$refs!: {
		areaFilter: AdvancedAreaFilter;
		eventOutcomeMultiSelect: EventOutcomeMultiSelect;
	};

	@Getter getPermissions: any;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@EventSearchStore.Action loadSearchableEventTypes: any;
	@EventSearchStore.Getter searchableEventTypes: any[];

	private readonly noUsersFound: string = "No users were found."
	private readonly defaultSearchMessage: string = "Type to search...";

	private readonly maxUserResultSize: number = 100;
	private readonly maxUserResultMessage: string = `Showing a maximum of ${this.maxUserResultSize} users, if you cannot find the user you are looking for - try refining your search.`;
	private readonly DateFormatErrorMessage: string = `The From Date must be earlier than the To Date`;

	private userQueryExecuted: boolean = false;
	private timezone: string = "UTC";

	private displayFromDateTime: string = new Date().toISOString();
	private displayToDateTime: string = new Date().toISOString();

	private fromDateTime: string = new Date().toISOString();
	private toDateTime: string = new Date().toISOString();

	private tmpFromDate: string = new Date().toISOString();
	private tmpToDate: string = new Date().toISOString();

	private filterApplied: boolean = false;

	private triggerUpdate: boolean = false;

	private canViewActivityLogHistory: boolean = false;
	private canViewActivityLogManagement: boolean = false;

	private showLiveData: boolean = true;
	private showAdvancedSearch: boolean = false;
	private isModalShown: boolean = false;

	private userList: UserInfo[] = [];

	private selectedEventTypes: EventType[] = [];
	private selectedOutcomeList: number[] = [];
	private activityLogOutcomeList: number[] = [];
	private selectedUserList: UserInfo[] = [];
	private selectedAreaList: AreaFilterItem[] = [];
	private noteQuery: string = "";
	private activityLogRequest: ActivityLogRequest;
	private userSearchToken: CancelTokenSource;

	private validEventTypes: EventType[] = [];
	private outcomeHasChildren: boolean = false;
	private maxNotesLength: number = 250;

	async created(): Promise<void> {
		// setup permissions
		this.populateRequest();

		this.canViewActivityLogHistory = this.getPermissions.canViewActivityLogHistory;
		this.canViewActivityLogManagement = this.getPermissions.canViewActivityLogManagement;

		// if user does not have access take them to the event queue
		if (!this.isActivityLogEnabled || !this.canViewActivityLogManagement) {
			await this.$router.push("/event-queue");
		}

		// set timezone based on browser
		this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		if (this.isNonActivityLogEventsEnabled) {
			await this.setUpSearchableEventTypes();
		}
	}

	async setUpSearchableEventTypes(): Promise<void> {
		await this.loadSearchableEventTypes()
		this.validEventTypes = this.searchableEventTypes;

		this.validEventTypes = this.validEventTypes.filter(r => r.eventTypeID != EventTypes.Patrol && r.eventTypeID != EventTypes.GuardTour);
		const defaultEventType: EventType = this.validEventTypes
			.find(({ eventTypeID }) => eventTypeID === EventTypes.ActivityLog);

		if(defaultEventType) this.selectedEventTypes.push(defaultEventType)
	}

	private showModal(): void {
		this.showLiveData = false;
		this.isModalShown = true;
	}

	private onSetDate(dates): void {
		if (dates) {
			try {
				this.displayFromDateTime = dates.minDate ? new Date(dates.minDate).toISOString() : new Date().toISOString();
			} catch {
				this.displayFromDateTime = new Date().toISOString();
			}
			this.displayToDateTime = this.showLiveData ? new Date().toISOString() : new Date(dates.maxDate).toISOString();
		}
	}

	private onDateReset(): void {
		let startOfDay = new Date();
		startOfDay.setHours(0);
		startOfDay.setMinutes(0);
		startOfDay.setSeconds(0);
		startOfDay.setMilliseconds(0);

		this.tmpFromDate = startOfDay.toISOString();
		this.tmpToDate = new Date().toISOString();
	}

	private setDateRange(): void {
		this.fromDateTime = this.tmpFromDate;
		this.toDateTime = this.showAdvancedSearch ? this.tmpToDate : this.tmpFromDate;

		// if doing the standard day's search
		if (!this.showAdvancedSearch) {
			// set the start to be 00:00:00.000
			let startOfDay = new Date(this.fromDateTime);
			startOfDay.setHours(0);
			startOfDay.setMinutes(0);
			startOfDay.setSeconds(0);
			startOfDay.setMilliseconds(0);

			// set the end to be 23:59:59.999
			let endOfDay = new Date(this.toDateTime);
			endOfDay.setHours(23);
			endOfDay.setMinutes(59);
			endOfDay.setSeconds(59);
			endOfDay.setMilliseconds(999);

			this.fromDateTime = startOfDay.toISOString();
			this.toDateTime = endOfDay.toISOString();
		}
	}

	private resetLiveStatus(): void {
		this.showLiveData = !this.showLiveData;
		this.triggerUpdate = false;
	}

	private updateLiveStatus(status: boolean): void {
		this.showLiveData = status;
	}

	public get isActivityLogEnabled(): boolean {
		return this.getFeature(["ActivityLog"]);
	}

	public get isActivityLogAdvancedFilteringEnabled(): boolean {
		return this.getFeature(["ActivityLog", "AdvancedFiltering"]);
	}

	public get isNonActivityLogEventsEnabled(): boolean {
		return this.getFeature( ["ActivityLog", "NonActivityLogEvents"]);
	}

	public get isDateRangeValid(): boolean {
		if (this.showAdvancedSearch) {
			return Date.parse(this.tmpFromDate) < Date.parse(this.tmpToDate);
		}

		return true;
	}

	private clearFilters(): void {
		this.selectedOutcomeList = [];
		this.selectedUserList = [];
		this.noteQuery = "";
		this.activityLogRequest.advancedSearch = null;
		this.onDateReset();
		this.filterApplied = false;
		this.$refs.areaFilter.reset();
		this.$refs.eventOutcomeMultiSelect.reset();
	}

	private async onSubmit(): Promise<void> {
		this.filterApplied = true;
		this.populateRequest();
		this.triggerUpdate = true;
		this.isModalShown = false;
	}

	private onAreaChange(selectedAreas: AreaFilterItem[]): void {
		this.selectedAreaList = selectedAreas
	}

	private onOutcomeChange(selectedOutcomes: number[], activityLogOutcomes: number[]): void {
		this.activityLogOutcomeList = activityLogOutcomes;
		this.selectedOutcomeList = selectedOutcomes;
	}

	private onEventListChange(): void {
		this.activityLogRequest.eventTypeIds = this.selectedEventTypes.map(r => r.eventTypeID);

		if (this.activityLogRequest.advancedSearch &&
			(!this.isNonActivityLogEventsEnabled || !this.selectedEventTypes.some(r => r.eventTypeID == EventTypes.Alarm))) {
			this.activityLogRequest.advancedSearch.outcomeFilterList = this.activityLogOutcomeList;
		}

		this.triggerUpdate = true;
	}

	private handleUserFeedback(): string {
		if (this.userQueryExecuted) {
			return this.noUsersFound;
		}

		return this.defaultSearchMessage;
	}

	private async handleInvolvedPartySearchInput(searchTerm: string): Promise<void> {
		if (!this.isActivityLogAdvancedFilteringEnabled || !this.showAdvancedSearch) {
			return;
		}

		if (isNullOrWhitespace(searchTerm)) {
			this.userList = [];
			this.userQueryExecuted = false;
			return;
		}

		this.userQueryExecuted = true;
		await this.performUserSearch(searchTerm);
	}

	private performUserSearch: (searchTeam: string) => Promise<void> = debounce(
		async function(searchTerm: string) {
			if (this.userSearchToken) {
				this.userSearchToken.cancel("Cancel Search");
			}

			let includeSystemAdmins: boolean = true;

			this.userSearchToken = axios.CancelToken.source();

			try {
				this.userList = await api.getUsersInfoBySearchTerm(searchTerm, UserTypeFilterEnum.UsersOnly, this.maxUserResultSize, includeSystemAdmins);
			} catch (e) {
				return; //Return when past requests are cancelled
			}
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
	);

	private populateRequest(): void {
		this.setDateRange();

		let fromDate = this.fromDateTime;
		let toDate = this.toDateTime;

		this.activityLogRequest = {
			page: 1,
			pageSize: 25,
			eventTypeIds: this.selectedEventTypes.map(r => r.eventTypeID),
			fromDate: fromDate,
			toDate: toDate,
			advancedSearch: null
		}

		if (!this.showAdvancedSearch) {
			this.activityLogRequest.advancedSearch = null
			return;
		}

		if(this.isActivityLogAdvancedFilteringEnabled){
			this.activityLogRequest.advancedSearch = {
				areaFilterList: this.selectedAreaList,
				userFilterList: this.selectedUserList.map(r => r.userId),
				outcomeFilterList: this.selectedOutcomeList,
				noteFilter: this.noteQuery,
			}
		}
	}

	private get isActivityLogOutcomes(): boolean {
		return !(this.isNonActivityLogEventsEnabled && this.selectedEventTypes.some(r => r.eventTypeID == EventTypes.Alarm))
	}
}
